import React, { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { Box, getCSSVariable, Link, PressableBox, Text } from '@opendoor/bricks-next';
import { IconHome, IconLogout2 } from '@tabler/icons-react';
import { useBuyerHubFlagStatus } from 'helpers/useBuyerHubFlag';
import { navTransactionLabel, useNavTransactions } from 'helpers/useNavTransactions';

import { useObservability } from '../../helpers/observability';
import { useHasActiveBuyerOffers } from '../../helpers/useBuyerOffers';
import type { Lead } from '../../helpers/useLeads';
import { useLeads } from '../../helpers/useLeads';
import { getSessionExtras } from '../api';
import { navTransactionIcon } from './navTransactionIcon';

interface AccountMenuProps {
  isExpanded: boolean;
  isLoggedIn: boolean;
}

const HELP_CENTER_URL = 'https://help.opendoor.com';

/**
 * The signed-in dropdown is gated behind the buyer-hub rollout flag:
 * - `enabled`  → the redesigned active-transactions menu.
 * - `disabled` → the previous leads/resale/active-offer menu (so users outside
 *                the rollout are unchanged).
 * - `loading`  → only the static footer renders, so neither variant's data
 *                queries fire before the flag resolves.
 */
const AccountMenu = ({ isExpanded, isLoggedIn }: AccountMenuProps) => {
  const hubFlagStatus = useBuyerHubFlagStatus();

  if (!isLoggedIn) {
    return (
      <PrimaryNavSubmenu isExpanded={isExpanded} isLoggedIn={false}>
        <PrimaryNavItem>
          <MenuItem href={HELP_CENTER_URL}>Help center</MenuItem>
        </PrimaryNavItem>
      </PrimaryNavSubmenu>
    );
  }

  return (
    <PrimaryNavSubmenu isExpanded={isExpanded} isLoggedIn>
      {hubFlagStatus === 'enabled' && <ActiveTransactionItems />}
      {hubFlagStatus === 'disabled' && <LegacyAccountItems isExpanded={isExpanded} />}
      <AccountMenuFooter />
    </PrimaryNavSubmenu>
  );
};

/** Redesigned menu (flag on): the customer's active purchases and sales. */
const ActiveTransactionItems: React.FC = () => {
  const transactions = useNavTransactions();
  const iconColor = getCSSVariable('$contentPrimary', 'color');

  return (
    <>
      {transactions.map((transaction) => {
        const Icon = navTransactionIcon(transaction.type);
        return (
          <PrimaryNavItem key={transaction.id}>
            <MenuItem
              href={transaction.href}
              icon={<Icon size={20} stroke={1.5} color={iconColor} />}
            >
              {navTransactionLabel(transaction)}
            </MenuItem>
          </PrimaryNavItem>
        );
      })}
    </>
  );
};

/** Previous menu (flag off): leads, resale dashboard, and active-offer dashboard. */
const LegacyAccountItems: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => {
  const { trackEvent } = useObservability();
  const { user: human } = useAuth();
  const [hasResaleDashboard, setHasResaleDashboard] = useState(false);
  const leads = useLeads();
  const { hasActiveOffers } = useHasActiveBuyerOffers(isExpanded);
  const hasLeads = leads && leads.length > 0;
  const iconColor = getCSSVariable('$contentPrimary', 'color');

  useEffect(() => {
    if (!human) {
      return;
    }
    getSessionExtras().then((extras) => {
      setHasResaleDashboard(extras.has_resale_closing_dashboard);
    });
  }, [human?.humanToken, human?.customerUuid]);

  return (
    <>
      {hasLeads &&
        leads.slice(0, 3).map((lead: Lead) => (
          <PrimaryNavItem key={lead.id}>
            <MenuItem href={lead.url}>
              {lead.address.street}
              {lead.readable_offer_state && ` (${lead.readable_offer_state})`}
            </MenuItem>
          </PrimaryNavItem>
        ))}
      {leads && leads.length > 3 && (
        <PrimaryNavItem>
          <MenuItem href="/properties">More</MenuItem>
        </PrimaryNavItem>
      )}
      {hasLeads && <Divider />}
      {hasResaleDashboard && (
        <PrimaryNavItem>
          <MenuItem href="/closing-dashboard">Closing dash</MenuItem>
        </PrimaryNavItem>
      )}
      {hasResaleDashboard && <Divider />}
      {hasActiveOffers && (
        <PrimaryNavItem>
          <MenuItem
            icon={<IconHome size={20} stroke={1.5} color={iconColor} />}
            onClick={() =>
              trackEvent('cta-click', 'dashboard-link', undefined, {
                categoryOverride: 'homepage',
              })
            }
            href="/dashboard"
          >
            Dashboard
          </MenuItem>
        </PrimaryNavItem>
      )}
    </>
  );
};

/** Account, Help center, and Sign out — shared by both signed-in variants. */
const AccountMenuFooter: React.FC = () => {
  const { trackEvent } = useObservability();
  const { logout } = useAuth();
  const iconColor = getCSSVariable('$contentPrimary', 'color');

  const handleSignOutClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  return (
    <>
      <PrimaryNavItem>
        <MenuItem
          // TODO: remove categoryOverride
          // observability is initialized with 'cosmos' as the category for all events
          onClick={() =>
            trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
          }
          href="/account"
        >
          Account
        </MenuItem>
      </PrimaryNavItem>
      <Divider />
      <PrimaryNavItem>
        <MenuItem href={HELP_CENTER_URL}>Help center</MenuItem>
      </PrimaryNavItem>
      <PrimaryNavItem>
        <MenuButton
          onPress={handleSignOutClick}
          label="Sign out"
          icon={<IconLogout2 size={20} stroke={1.5} color={iconColor} />}
          analyticsName="cosmos-account-menu-sign-out"
        />
      </PrimaryNavItem>
    </>
  );
};

const PrimaryNavSubmenu: React.FC<{
  isExpanded: boolean;
  isLoggedIn: boolean;
  children: React.ReactNode;
}> = ({ isExpanded, isLoggedIn, children }) => (
  <Box
    {...(!isExpanded ? { inert: '' } : {})}
    position="absolute"
    // 100% clears the avatar; +12px covers the remaining space between the
    // avatar bottom and the header bar bottom (avatar is vertically centered)
    top="calc(100% + 12px)"
    right={0}
    backgroundColor="$backgroundPrimary"
    borderRadius="$6x"
    borderWidth={1}
    borderColor="$borderPrimary"
    minWidth={isLoggedIn ? 268 : 198}
    maxWidth={isLoggedIn ? 268 : 270}
    padding="$2x"
    gap="$2x"
    overflow="hidden"
    zIndex={1000}
    opacity={isExpanded ? 1 : 0}
    aria-hidden={!isExpanded}
    tag="ul"
    style={{
      visibility: isExpanded ? 'visible' : 'hidden',
      transition: 'opacity 0.2s ease, visibility 0.2s ease',
      listStyle: 'none',
      margin: 0,
    }}
  >
    {children}
  </Box>
);

const PrimaryNavItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box tag="li">{children}</Box>
);

const Divider = () => <Box role="separator" height={1} backgroundColor="$borderSecondary" />;

interface MenuItemProps {
  href: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ href, children, icon, onClick }) => (
  <Link
    href={href}
    onPress={onClick}
    analyticsName=""
    color="$contentPrimary"
    display="flex"
    width="100%"
  >
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      gap="$4x"
      paddingVertical={icon ? '$5x' : '$4x'}
      paddingRight={icon ? '$4x' : '$6x'}
      paddingLeft={icon ? '$4x' : 36}
      borderRadius="$4x"
      hoverStyle={{ backgroundColor: '$backgroundStateHoverPrimary' }}
      cursor="pointer"
      width="100%"
    >
      {icon && (
        <Box width={20} height={20} display="flex" alignItems="center" justifyContent="center">
          {icon}
        </Box>
      )}
      <Text
        tag="span"
        typography="$bodyMedium"
        color="$contentPrimary"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        minWidth={0}
        flexShrink={1}
      >
        {children}
      </Text>
    </Box>
  </Link>
);

interface MenuButtonProps {
  onPress: () => void;
  label: string;
  icon?: React.ReactNode;
  analyticsName: string;
}

const MenuButton: React.FC<MenuButtonProps> = ({ onPress, label, icon, analyticsName }) => (
  <PressableBox
    onPress={onPress}
    display="flex"
    flexDirection="row"
    alignItems="center"
    justifyContent="flex-start"
    gap="$4x"
    paddingVertical="$4x"
    paddingRight="$6x"
    paddingLeft={icon ? '$4x' : 36}
    borderRadius="$4x"
    hoverStyle={{ backgroundColor: '$backgroundStateHoverPrimary' }}
    cursor="pointer"
    width="100%"
    aria-label={label}
    analyticsName={analyticsName}
  >
    {icon && (
      <Box width={20} height={20} display="flex" alignItems="center" justifyContent="center">
        {icon}
      </Box>
    )}
    <Text
      tag="span"
      typography="$bodyMedium"
      color="$contentPrimary"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      minWidth={0}
      flexShrink={1}
    >
      {label}
    </Text>
  </PressableBox>
);

export default AccountMenu;
